import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useColors } from '@mastimanch/ui-components';
import { useAuthContext } from '../auth-config';
import toast from 'react-hot-toast';
import { FiMail, FiLock, FiEye, FiEyeOff, FiArrowRight } from 'react-icons/fi';

const LoginPage: React.FC = () => {
  const colors = useColors();
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, signOut } = useAuthContext();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Get return path
  const from = (location.state as any)?.from || '/dashboard';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields.');
      return;
    }

    try {
      setIsLoading(true);
      await signIn({ email, password });

      // Check if registration is completed
      const regCheckResponse = await fetch(`/api/auth/check-registration?email=${encodeURIComponent(email)}`);
      if (regCheckResponse.ok) {
        const regStatus = await regCheckResponse.json();
        if (!regStatus.isCompleted) {
          toast.error('Your registration is incomplete. Redirecting to complete it.');
          await signOut();
          navigate('/register', { state: { resumeEmail: email, step: regStatus.currentStep } });
          return;
        }
      }

      toast.success('Successfully logged in!');
      navigate(from, { replace: true });
    } catch (err: any) {
      console.error('Login error:', err);
      toast.error(err.message || 'Incorrect email or password.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-6 bg-gradient-to-br from-[#fffbeb] via-[#ffe4e6] to-[#e0f2fe] overflow-hidden select-none font-sans">

      {/* Background SVG illustrations representing learning/fun */}
      <div className="absolute top-10 left-10 w-28 h-28 opacity-10 pointer-events-none text-[#ff6b35]">
        <svg fill="currentColor" viewBox="0 0 24 24"><path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3z" /></svg>
      </div>
      <div className="absolute bottom-10 right-10 w-36 h-36 opacity-10 pointer-events-none text-[#1b4ddb]">
        <svg fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H7c0-2.76 2.24-5 5-5s5 2.24 5 5c0 1.04-.42 1.99-1.07 2.75z" /></svg>
      </div>

      {/* Main Card */}
      <div className="relative w-full max-w-md bg-white/80 backdrop-blur-md rounded-3xl shadow-2xl border border-white/50 p-8 space-y-6 transition-all duration-300 z-10">
        
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center mb-1">
            <img 
              alt="Masti Manch Logo" 
              className="h-14 object-contain" 
              src="/images/mmlogo.png"
            />
          </div>
          <span className="text-3xl block">🎒</span>
          <h2 className="text-2xl font-extrabold text-[#1a2d54]">Welcome Back!</h2>
          <p className="text-sm text-gray-500 font-medium">Log in to your student account to continue learning.</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-wide text-gray-400">
              Email Address
            </label>
            <div className="relative">
              <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input 
                type="email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e.g. student@school.com"
                disabled={isLoading}
                className="w-full pl-12 pr-4 py-3.5 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium text-sm text-[#1b2559] bg-white/70 transition-all"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <label className="text-xs font-bold uppercase tracking-wide text-gray-400">
                Password
              </label>
              <Link 
                to="/forgot-password" 
                className="text-xs font-semibold text-[#1b4ddb] hover:underline"
              >
                Forgot Password?
              </Link>
            </div>
            <div className="relative">
              <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input 
                type={showPassword ? 'text' : 'password'} 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={isLoading}
                className="w-full pl-12 pr-12 py-3.5 rounded-2xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium text-sm text-[#1b2559] bg-white/70 transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isLoading}
              >
                {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-4 rounded-2xl text-white font-bold bg-[#1b4ddb] hover:bg-blue-700 transition-all flex items-center justify-center space-x-2 text-sm shadow-md active:scale-95 transform"
          >
            {isLoading ? (
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            ) : (
              <>
                <span>Log In</span>
                <FiArrowRight size={16} />
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="pt-4 border-t border-gray-100 text-center text-xs font-semibold text-gray-500">
          <span>Don't have an account? </span>
          <Link to="/register" className="text-[#1b4ddb] hover:underline font-bold ml-1">
            Sign up now
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
